import { ClockCircleOutlined } from '@ant-design/icons';
import { CalendarIcon } from '@heroicons/react/outline';
import { Avatar, Progress, Skeleton } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import NumberIcons from 'assets/Icons/Numbers';
import Image from 'next/image';

type SkeletonCardProps = {
  variant:
    | 'home'
    | 'rank'
    | 'continue'
    | 'shopBookCard'
    | 'bundleShop'
    | 'courseShop'
    | 'schoolShop'
    | 'courseListing'
    | 'liveBannerCard';
  index?: number;
};

const SkeletonCard = ({ variant, index }: SkeletonCardProps) => {
  const screens = useBreakpoint();

  if (variant === 'home') {
    return (
      <div className='flex justify-center'>
        <div className='relative'>
          <Skeleton.Button
            active
            style={{
              width: screens.md ? 168 : 108,
              height: screens.md ? 238 : 153,
              borderRadius: screens.md ? 8 : 4,
            }}
          />
        </div>
      </div>
    );
  }

  if (variant === 'rank') {
    const imageWidth = screens.md ? 113 : 72;
    const imageHeight = screens.md ? 160 : 102;
    const RankIcon = NumberIcons[(index || 0) + 1];

    return (
      <div className='relative flex items-center justify-center'>
        {RankIcon && (
          <div
            className='absolute bottom-0 left-0 z-0 pointer-events-none select-none'
            style={{
              width: screens.md ? 124 : 63,
              height: screens.md ? 160 : 88,
            }}
          >
            <Image
              src={RankIcon}
              alt={`Rank ${(index || 0) + 1}`}
              fill
              className='object-contain'
            />
          </div>
        )}

        <div
          style={{ width: imageWidth, height: imageHeight }}
          className='relative ml-[40px] md:ml-20 flex-shrink-0 z-20 bg-cartBgColor'
        >
          <Skeleton.Image
            active
            style={{ width: imageWidth, height: imageHeight, borderRadius: 8 }}
          />
        </div>
      </div>
    );
  }

  if (variant === 'continue') {
    return (
      <div className='w-full max-w-xs rounded-lg bg-cartBgColor border border-[#282829] overflow-hidden min-w-[260px] md:min-w-[350px] xl:min-w-[400px]'>
        <div className='flex items-center h-full'>
          <div className='p-3 bg-[#121212] rounded'>
            <Skeleton.Button
              active
              style={{
                width: screens.md ? 65 : 40,
                height: screens.md ? 95 : 71,
                borderRadius: 4,
              }}
            />
          </div>
          <div className='flex flex-col justify-between w-full px-3 py-2'>
            <Skeleton
              active
              paragraph={{ rows: 2, width: ['50%', '80%'] }}
              title={false}
              className='mb-2'
            />
            <div>
              <Skeleton.Input style={{ width: '50%' }} active size='small' />
              <Progress
                percent={60}
                showInfo={false}
                className='mt-2 customProcessBar'
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'shopBookCard') {
    return (
      <div className='w-full lg:p-6 overflow-hidden transition-all rounded-[6px]'>
        <div className='flex justify-center'>
          <Skeleton.Button
            active
            style={{
              width: 168,
              height: 238,
              borderRadius: 8,
            }}
          />
        </div>

        <div className='mt-3 flex justify-center'>
          <Skeleton.Input
            style={{
              width: 100,
              height: 20,
              borderRadius: 4,
            }}
            active
            size='small'
          />
        </div>

        <div className='mt-2 text-center'>
          <Skeleton.Input
            style={{ width: 120, height: 16, borderRadius: 4 }}
            active
            size='small'
          />
          <Skeleton.Input
            style={{ width: 80, height: 12, marginTop: 8, borderRadius: 4 }}
            active
            size='small'
          />
        </div>

        <div className='mt-3 flex justify-center space-x-3'>
          <Skeleton.Input className='w-full' active size='small' />
        </div>
      </div>
    );
  }

  if (variant === 'bundleShop') {
    return (
      <div className='w-full h-full border border-stroke rounded-xl flex flex-col justify-between overflow-hidden'>
        <div className='w-full aspect-[14/11] bg-[#1a1a1a] rounded-t-[12px] flex items-center justify-center overflow-hidden'>
          <Skeleton.Button
            active
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '12px 12px 0 0',
            }}
            className='w-full h-full'
          />
        </div>
        <div className='2xl:p-5 xl:p-4 lg:p-4 md:p-3 sm:p-2.5 p-2.5 flex flex-col justify-between flex-1 gap-2'>
          <Skeleton.Input
            active
            size='small'
            style={{
              width: 80,
              height: 16,
              borderRadius: 4,
            }}
          />
          <Skeleton.Input
            active
            size='small'
            style={{
              width: '85%',
              height: 22,
              borderRadius: 4,
            }}
          />
        </div>
      </div>
    );
  }
  if (variant === 'courseShop') {
    return (
      <div className='w-full h-full border border-cartBgColor hover:bg-[#151515] card rounded-[12px] flex flex-col transition duration-300'>
        <div className='h-full card rounded-md text-fadedWhite px-3 flex flex-col justify-between items-center w-full'>
          <div className='w-full max-h-[190px] py-3 md:pt-6 md:pb-0  flex justify-center h-full xl:p-5'>
            <Skeleton.Button
              active
              style={{
                width: '100%',
                height: 170,
                borderRadius: 12,
              }}
              className='w-full'
            />
          </div>

          <div className='w-full flex flex-col justify-center gap-y-1 py-4 md:py-0 items-center'>
            <Skeleton.Input active size='small' className='w-full md:mt-2' />

            {/* Price row */}
            <div className='flex items-center justify-center gap-3 mt-2 pb-4'>
              <Skeleton.Input active className='w-full' size='small' />
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (variant === 'liveBannerCard') {
    return (
      <div className='flex flex-col gap-y-4 md:gap-y-6  lg:flex-row h-full text-white rounded-2xl overflow-hidden w-full bg-black'>
        <div className='relative flex-1 w-full lg:w-[65%] 2xl:w-[50%] h-full aspect-[2/1]'>
          <Skeleton.Button
            active
            className='!w-full !h-full rounded-lg'
            style={{ height: '100%', width: '100%' }}
          />
        </div>

        <div className='flex justify-center lg:pl-8 w-full lg:w-[35%] 2xl:w-[50%] items-center'>
          <div className='flex flex-col justify-center h-full bg-black gap-y-4 md:gap-y-6 w-full'>
            <Skeleton.Button active size='small' style={{ width: 120 }} />
            <Skeleton.Input active size='large' style={{ width: '90%' }} />
            <Skeleton.Input active size='default' style={{ width: '95%' }} />
            <div className='flex items-center gap-4'>
              <ClockCircleOutlined className='text-[#FFFFFF99]' />
              <Skeleton.Input active size='small' style={{ width: 100 }} />
              <span className='text-white'>|</span>
              <CalendarIcon className='text-[#FFFFFF99] w-4 h-4' />
              <Skeleton.Input active size='small' style={{ width: 100 }} />
            </div>
            <div className='flex items-start gap-3'>
              <Avatar size={40} />
              <div className='flex flex-col gap-1'>
                <Skeleton.Input active size='small' style={{ width: 140 }} />
                <Skeleton.Button active size='small' style={{ width: 100 }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (variant === 'courseListing') {
    return (
      <div className='flex flex-col md:flex-row items-start gap-x-6 px-4 md:px-6 xl:px-8 mb-6 w-full'>
        {/* Thumbnail */}
        <div className='relative w-full aspect-[16/9] md:min-w-[250px] lg:max-w-[250px] xl:max-w-[355px] xl:min-w-[355px] rounded-sm overflow-hidden'>
          <Skeleton.Button
            active
            className='!w-full !h-full rounded-sm'
            style={{ height: '100%', width: '100%' }}
          />
        </div>

        <div className='mt-5 md:mt-0 flex flex-col items-start gap-2 self-stretch w-full'>
          <Skeleton.Button
            active
            size='small'
            style={{ width: 90, height: 20, borderRadius: 4 }}
          />

          <Skeleton.Input
            active
            className='w-1/2'
            size='large'
            style={{ width: '80%', height: 24 }}
          />

          <Skeleton.Input
            active
            size='small'
            className='w-full h-12 bg-[#1C1C1C]'
            style={{ width: '95%', height: 16 }}
          />
          <Skeleton.Input
            active
            size='small'
            className='w-[20%] h-5'
            style={{ width: '85%', height: 16 }}
          />

          <Skeleton.Input active size='small' className='w-[30%] h-5' />
          <Skeleton.Input
            active
            size='small'
            className='w-[40%] h-5'
            style={{ width: 120, height: 16 }}
          />
        </div>
      </div>
    );
  }
  return null;
};

export default SkeletonCard;
